import { formatList } from "@/features/tasks/lib/lists";
import { addDays, formatWeekday } from "@/features/time/lib/week";
import type { Schemas } from "@/shared/api/client";
import type { Locale, MessageId } from "@/shared/i18n/messages";
import { formatDay } from "@/shared/lib/format";

export type TaskSeries = Schemas["TaskSeriesOut"];
export type RecurrenceSet = Schemas["RecurrenceSet"];
export type Frequency = Schemas["RecurrenceFrequency"];

type Translate = (id: MessageId, values?: Record<string, string | number>) => string;

/** How a task repeats, as the panel edits it (the server's RecurrenceSet, every field set). */
export interface RepeatRule {
  frequency: Frequency;
  interval: number;
  /** 0 = Sunday ... 6 = Saturday, sorted (weekly rules). */
  weekdays: number[];
  /** 1-31 (monthly rules); past a month's end it falls on the last day. */
  month_day: number | null;
  /** A Friday or Saturday date moves to the Sunday after it. */
  workdays_only: boolean;
  ends_on: string | null;
  max_occurrences: number | null;
}

export const FREQUENCIES = ["daily", "weekly", "monthly"] as const satisfies readonly Frequency[];
/** The agency's week, Sunday first; the working days are Sunday to Thursday. */
export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;
export const WORKDAYS = [0, 1, 2, 3, 4] as const;
const LAST_DAY_OF_MONTH = 31;
const FRIDAY = 5;
/** A Sunday, so day n of the agency's week is this date plus n days. */
export const A_SUNDAY = "2026-10-04";

/** 0 = Sunday ... 6 = Saturday, for a calendar date such as "2026-10-08". */
export function weekdayOf(isoDate: string): number {
  const [year = 1970, month = 1, day = 1] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function dayOfMonth(isoDate: string): number {
  return Number(isoDate.slice(8, 10));
}

function sorted(days: readonly number[]): number[] {
  return [...new Set(days)].sort((a, b) => a - b);
}

/** The rule a series follows, with its defaults spelled out (the start's weekday or day). */
export function ruleOf(series: TaskSeries): RepeatRule {
  return {
    frequency: series.frequency,
    interval: series.interval,
    weekdays:
      series.frequency === "weekly"
        ? sorted(series.weekdays.length > 0 ? series.weekdays : [weekdayOf(series.starts_on)])
        : [],
    month_day:
      series.frequency === "monthly" ? (series.month_day ?? dayOfMonth(series.starts_on)) : null,
    workdays_only: series.workdays_only,
    ends_on: series.ends_on,
    max_occurrences: series.max_occurrences,
  };
}

/** The request body that saves a rule. */
export function toRecurrenceSet(rule: RepeatRule): RecurrenceSet {
  return {
    frequency: rule.frequency,
    interval: rule.interval,
    weekdays: rule.frequency === "weekly" ? sorted(rule.weekdays) : [],
    month_day: rule.frequency === "monthly" ? rule.month_day : null,
    workdays_only: rule.workdays_only,
    ends_on: rule.ends_on,
    max_occurrences: rule.max_occurrences,
  };
}

/** Whether the weekend rule changes anything: it does only for dates that can be weekends. */
function movesWeekends(rule: RepeatRule): boolean {
  return rule.workdays_only && canFallOnWeekend(rule);
}

/** Whether two rules repeat on the same days (their ends aside). */
export function sameRule(left: RepeatRule, right: RepeatRule): boolean {
  return (
    left.frequency === right.frequency &&
    left.interval === right.interval &&
    sorted(left.weekdays).join() === sorted(right.weekdays).join() &&
    left.month_day === right.month_day &&
    movesWeekends(left) === movesWeekends(right)
  );
}

export type PresetId = "workdays" | "weekly" | "biweekly" | "monthly";

const PLAIN: Omit<RepeatRule, "frequency" | "weekdays" | "month_day"> = {
  interval: 1,
  workdays_only: false,
  ends_on: null,
  max_occurrences: null,
};

/** Every week on the weekday of `anchor`: where a custom repeat starts from. */
export function weeklyOn(anchor: string): RepeatRule {
  return { ...PLAIN, frequency: "weekly", weekdays: [weekdayOf(anchor)], month_day: null };
}

/** The usual repeats, anchored on a date (the task's due date, or today). */
export function repeatPresets(anchor: string): { id: PresetId; rule: RepeatRule }[] {
  const weekday = weekdayOf(anchor);
  return [
    {
      id: "workdays",
      rule: { ...PLAIN, frequency: "weekly", weekdays: [...WORKDAYS], month_day: null },
    },
    { id: "weekly", rule: weeklyOn(anchor) },
    {
      id: "biweekly",
      rule: { ...PLAIN, frequency: "weekly", interval: 2, weekdays: [weekday], month_day: null },
    },
    {
      id: "monthly",
      rule: { ...PLAIN, frequency: "monthly", weekdays: [], month_day: dayOfMonth(anchor) },
    },
  ];
}

function isEveryWorkday(rule: RepeatRule): boolean {
  if (rule.interval !== 1) return false;
  if (rule.frequency === "daily") return rule.workdays_only;
  return rule.frequency === "weekly" && sorted(rule.weekdays).join() === WORKDAYS.join();
}

/** Whether a rule can land on a Friday or Saturday (only then does moving them matter). */
function canFallOnWeekend(rule: RepeatRule): boolean {
  if (isEveryWorkday(rule)) return false;
  return rule.frequency !== "weekly" || rule.weekdays.some((day) => day >= FRIDAY);
}

/** "Sunday" / "أحد": a weekday as it reads after "every". */
function weekdayName(day: number, t: Translate): string {
  return t(`planning.repeat.day.${day}` as MessageId);
}

/** "Sunday" / "الأحد": a weekday as it reads after "on". */
function weekdayOn(day: number, locale: Locale): string {
  return formatWeekday(addDays(A_SUNDAY, day), locale, "long");
}

function frequencySentence(rule: RepeatRule, t: Translate, locale: Locale): string {
  const interval = rule.interval;
  if (rule.frequency === "daily") return t("planning.repeat.daily", { interval });
  if (rule.frequency === "weekly") {
    const days = sorted(rule.weekdays);
    if (interval === 1) {
      const weekdays = formatList(
        days.map((day) => weekdayName(day, t)),
        locale,
      );
      return t("planning.repeat.weeklyEvery", { weekdays });
    }
    const weekdays = formatList(
      days.map((day) => weekdayOn(day, locale)),
      locale,
    );
    return t("planning.repeat.weekly", { interval, weekdays });
  }
  const day = rule.month_day ?? 1;
  return day >= LAST_DAY_OF_MONTH
    ? t("planning.repeat.monthlyLast", { interval })
    : t("planning.repeat.monthly", { interval, day });
}

/**
 * A rule in words: "Every Sunday and Wednesday" / "كل أحد وأربعاء", "Every 2 weeks on Sunday",
 * "Every month on day 15, until 31 Dec 2026", "كل يومين، ٥ مرات". Counts use the catalog's
 * plural forms, so Arabic reads right for every number.
 */
export function describeRecurrence(rule: RepeatRule, t: Translate, locale: Locale): string {
  let sentence = isEveryWorkday(rule)
    ? t("planning.repeat.everyWorkday")
    : frequencySentence(rule, t, locale);
  if (rule.workdays_only && canFallOnWeekend(rule)) {
    sentence = t("planning.repeat.offWeekends", { rule: sentence });
  }
  if (rule.ends_on !== null) {
    return t("planning.repeat.until", { rule: sentence, date: formatDay(rule.ends_on, locale) });
  }
  if (rule.max_occurrences !== null) {
    return t("planning.repeat.times", { rule: sentence, count: rule.max_occurrences });
  }
  return sentence;
}

/** The id of a series shown before the server has made it. */
export const PENDING_SERIES_ID = "pending";

/** The lead days the server uses when a repeat does not say. */
export const DEFAULT_LEAD_DAYS = 3;

/**
 * How a task's series looks right after a change, before the server answers: the new rule on
 * the series it already has (or a new one starting on `startsOn`). The next date is unknown
 * until the server works it out.
 */
export function optimisticSeries(
  series: TaskSeries | null,
  rule: RepeatRule,
  startsOn: string,
): TaskSeries {
  return {
    id: series?.id ?? PENDING_SERIES_ID,
    version: series?.version ?? 0,
    starts_on: series?.starts_on ?? startsOn,
    lead_days: series?.lead_days ?? DEFAULT_LEAD_DAYS,
    next_on: null,
    frequency: rule.frequency,
    interval: rule.interval,
    weekdays: rule.frequency === "weekly" ? sorted(rule.weekdays) : [],
    month_day: rule.frequency === "monthly" ? rule.month_day : null,
    workdays_only: rule.workdays_only,
    ends_on: rule.ends_on,
    max_occurrences: rule.max_occurrences,
  };
}
